import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { publicData } from '@/lib/publicData';

// A tiny local brand image replaces stock-photo / remote fallback downloads.
const DEFAULT_HERO_BANNERS = [
  {
    src: '/assets/ogmini-hero-960.webp',
    alt: 'OGMini — Be the OG',
    width: 960,
    height: 800,
    link: '',
  },
];
const HERO_SIZES = '(min-width: 1280px) 560px, calc((100vw - 160px) / 2)';

// Round 547 → "500+", 32 → "30+", 6 → "5+" so the hero never shows odd
// numbers like "547 Models" — keeps the "always growing" vibe without
// hand-managing the copy.
const niceRound = (n) => {
  if (!n) return '—';
  if (n < 10) return `${n}+`;
  if (n < 100) return `${Math.floor(n / 5) * 5}+`;
  if (n < 1000) return `${Math.floor(n / 50) * 50}+`;
  return `${Math.floor(n / 100) * 100}+`;
};

export default function HeroSection() {
  // Mobile + reduced-motion users skip the staggered framer-motion
  // entrance — paints instantly with final styles. Solves the
  // "first-scroll lag" complaint where the animation queue and a
  // user-initiated scroll were fighting for the main thread on cold
  // load.
  const [desktop, setDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktop(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  // Pull live catalogue counts so the "500+ Models · 15+ Brands · 20+
  // Collections" strip never lies. /api/stats is public, cached server-side.
  const [stats, setStats] = useState({ products: 0, brands: 0, collections: 0 });
  // Hero banner index — rotates every 5s with a crossfade between images.
  const [bannerIdx, setBannerIdx] = useState(0);
  // Admin-managed banners from /api/homepage-banners; falls back to the
  // hardcoded defaults when the admin hasn't uploaded any.
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    let alive = true;
    publicData('stats').then((data) => {
      if (alive) setStats({
        products: data.products || 0,
        brands: data.brands || 0,
        collections: data.collections || 0,
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!desktop) return undefined;
    let alive = true;
    const base = process.env.REACT_APP_BACKEND_URL;
    const asset = (path) => new URL(path, base).href;
    publicData('homepage-banners').then((data) => {
      if (!alive) return;
      const rows = (data.banners || []).filter(b => b.image_path).map((b) => ({
        src: asset(b.display_image_path || b.image_path),
        srcSet: b.image_sources?.map(s => `${asset(s.path)} ${s.width}w`).join(', ') || undefined,
        width: b.image_width || 800,
        height: b.image_height || 600,
        alt: b.alt || 'OGMini banner',
        link: b.link || '',
      }));
      const next = rows.length > 0 ? rows : DEFAULT_HERO_BANNERS;
      setBanners(next);
      setBannerIdx(i => Math.min(i, next.length - 1));
    }).catch(() => { if (alive) setBanners(DEFAULT_HERO_BANNERS); });
    return () => { alive = false; };
  }, [desktop]);

  useEffect(() => {
    if (banners.length < 2 || !desktop || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    // Wait 6 seconds before kicking off the rotation — gives the
    // browser time to finish parsing/painting the initial frame so
    // the very-first scroll on mobile doesn't compete with a banner
    // crossfade. Then rotate every 5s after that.
    const start = setTimeout(() => {
      if (!document.hidden && window.scrollY < window.innerHeight) setBannerIdx((i) => (i + 1) % banners.length);
    }, 6000);
    const id = setInterval(() => { if (!document.hidden && window.scrollY < window.innerHeight) setBannerIdx((i) => (i + 1) % banners.length); }, 11000);
    return () => { clearTimeout(start); clearInterval(id); };
  }, [banners.length, desktop]);

  const heroStats = [
    { num: niceRound(stats.products), label: 'Models', exact: stats.products },
    { num: niceRound(stats.brands), label: 'Brands', exact: stats.brands },
    { num: niceRound(stats.collections), label: 'Collections', exact: stats.collections },
  ];

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[80vh] flex items-center overflow-hidden bg-[#050505]">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div>
              <h1 data-testid="hero-tagline" className="font-mono text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase text-[#FF2A2A] mb-4 block">OGMini - Hyderabad&apos;s Premium Diecast Experience Store</h1>
            </div>

            <p data-testid="home-heading"
              className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.9] mb-6 text-white">
              Scale Down.<br /><span className="text-[#FF2A2A]">Revv Up.</span>
            </p>

            <p data-testid="hero-description"
              className="text-white/50 text-base leading-relaxed max-w-md mb-8">
              Premium 1:64 scale diecast collectibles. Hot Wheels, Mini GT, Tarmac Works &amp; beyond. Free shipping over ₹ 1,999.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/brands" data-testid="hero-brands-btn"
                className="group flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#FF2A2A] text-white font-bold uppercase tracking-wider text-sm px-6 py-3.5 hover:bg-[#FF4D4D] transition-all duration-300">
                Brands <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/pre-orders" data-testid="hero-preorders-btn"
                className="group flex-1 sm:flex-none flex items-center justify-center gap-2 bg-transparent text-white font-bold uppercase tracking-wider text-sm px-6 py-3.5 border border-white/30 hover:border-amber-400 hover:text-amber-400 transition-all duration-300">
                Pre-Orders <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="flex gap-10 mt-10">
              {heroStats.map(s => (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} title={s.exact ? `${s.exact} ${s.label.toLowerCase()} live in catalogue` : undefined}>
                  <div className="font-heading text-xl font-black text-white">{s.num}</div>
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/40 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="relative hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-lg">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl aspect-[4/3] bg-[#050505]" data-testid="hero-banner-stack">
                {desktop && banners.map((b, i) => {
                  if (i !== bannerIdx) return null;
                  const img = (
                    <img
                      key={b.src}
                      src={b.src}
                      srcSet={b.srcSet}
                      sizes={b.srcSet ? HERO_SIZES : undefined}
                      alt={b.alt}
                      data-testid={i === 0 ? 'hero-car-image' : `hero-banner-${i}`}
                      loading="eager"
                      fetchPriority={i === 0 ? 'high' : 'low'}
                      decoding="async"
                      width={b.width}
                      height={b.height}
                      onError={(e) => {
                        const image = e.currentTarget;
                        if (!image.dataset.fallback) {
                          image.dataset.fallback = '1';
                          image.removeAttribute('srcset');
                          image.removeAttribute('sizes');
                          image.src = DEFAULT_HERO_BANNERS[0].src;
                        }
                      }}
                      className="absolute inset-0 w-full h-full object-contain"
                      style={{ opacity: bannerIdx === i ? 1 : 0, pointerEvents: bannerIdx === i ? 'auto' : 'none' }}
                    />
                  );
                  // Wrap the active banner in its click-through link.
                  // Internal `/...` paths use react-router Link, externals
                  // open in a new tab.
                  if (b.link && bannerIdx === i) {
                    const isExternal = /^https?:\/\//i.test(b.link);
                    return isExternal ? (
                      <a data-testid={`hero-banner-link-${i}`} key={`${b.src}-link`} href={b.link} target="_blank" rel="noopener noreferrer"
                        className="absolute inset-0 block">
                        {img}
                      </a>
                    ) : (
                      <Link data-testid={`hero-banner-link-${i}`} key={`${b.src}-link`} to={b.link} className="absolute inset-0 block">
                        {img}
                      </Link>
                    );
                  }
                  return img;
                })}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/50 via-transparent to-transparent pointer-events-none" />
                {banners.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="hero-banner-dots">
                    {banners.map((b, i) => (
                      <button
                        key={`dot-${b.src || b.alt || i}`}
                        type="button"
                        aria-label={`Show banner ${i + 1}`}
                        aria-pressed={bannerIdx === i}
                        data-testid={`hero-banner-dot-${i}`}
                        onClick={() => setBannerIdx(i)}
                        className={`h-1 rounded-full transition-all ${bannerIdx === i ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/shop?new=24h"
                data-testid="hero-new-arrivals-chip"
                className="absolute -bottom-3 -right-3 inline-flex items-center gap-1 bg-[#FF2A2A] text-white px-5 py-2 text-[10px] font-bold tracking-[0.2em] uppercase shadow-lg hover:bg-[#FF4D4D] transition-colors"
                title="Just added in the last 24 hours"
              >
                New Arrivals <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
